'use client';

/**
 * Native app opening animation.
 *
 * Renders a full-screen overlay that assembles the DocuPro logo from small
 * pieces flying into place, then fades out. It only runs inside the Capacitor
 * app (never on the website) and owns its own timing so the assembly is shown
 * *after* the static native splash is hidden (otherwise it would play behind
 * it and never be seen):
 *
 *   1. mount               → render nothing (native splash still covering)
 *   2. detect native       → paint the overlay (purple, pieces hidden) under it
 *   3. hide native splash   → our overlay becomes the cover (seamless, same color)
 *   4. play                → pieces fly in and assemble the logo
 *   5. out → done          → fade the overlay away and unmount
 *
 * The overlay background matches the native Capacitor splash (#5d18ea).
 */
import { useEffect, useState, type CSSProperties } from 'react';

const GRID = 5; // 5×5 = 25 pieces
const LOGO_PX = 168; // assembled logo size
const TILE = LOGO_PX / GRID;

type Phase = 'idle' | 'cover' | 'play' | 'out' | 'done' | 'spinner';

// Marks that the opening logo animation has played this app session. Stored in
// sessionStorage so it plays once per cold launch (sessionStorage is cleared
// when the OS kills the app) but is skipped on in-app full reloads — e.g. the
// city switch does window.location.assign — where a plain spinner shows instead.
const SPLASH_SHOWN_KEY = 'dp_splash_shown';

// Deterministic pseudo-random so the scatter pattern is stable.
function seeded(n: number): number {
  const x = Math.sin(n * 99.13) * 43758.5453;
  return x - Math.floor(x);
}

const TILES = (() => {
  const out: { key: number; style: CSSProperties }[] = [];
  for (let r = 0; r < GRID; r += 1) {
    for (let c = 0; c < GRID; c += 1) {
      const i = r * GRID + c;
      out.push({
        key: i,
        style: {
          width: `${TILE}px`,
          height: `${TILE}px`,
          backgroundPosition: `-${c * TILE}px -${r * TILE}px`,
          '--dp-dx': `${(seeded(i + 1) - 0.5) * 300}px`,
          '--dp-dy': `${(seeded(i + 31) - 0.5) * 300}px`,
          '--dp-rot': `${(seeded(i + 61) - 0.5) * 120}deg`,
          animationDelay: `${seeded(i + 91) * 0.4}s`,
        } as CSSProperties,
      });
    }
  }
  return out;
})();

export function SplashAnimation() {
  const [phase, setPhase] = useState<Phase>('idle');
  // Spinner mode = a later in-app load, not the first cold launch.
  const [isSpinner, setIsSpinner] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const timers: ReturnType<typeof setTimeout>[] = [];

    (async () => {
      let native = false;
      try {
        const { Capacitor } = await import('@capacitor/core');
        native = Capacitor.isNativePlatform();
      } catch {
        native = false;
      }
      if (cancelled) return;
      if (!native) {
        setPhase('done'); // never show on the website
        return;
      }

      // Already played the opening animation this session? Then this is a
      // later in-app load (e.g. a full reload) — show a plain spinner, not the
      // logo assembly, and bow out quickly.
      let alreadyShown = false;
      try {
        alreadyShown = sessionStorage.getItem(SPLASH_SHOWN_KEY) === '1';
      } catch {
        alreadyShown = false;
      }

      if (alreadyShown) {
        setIsSpinner(true);
        setPhase('spinner');
        try {
          const { SplashScreen } = await import('@capacitor/splash-screen');
          await SplashScreen.hide({ fadeOutDuration: 150 });
        } catch {
          /* not available — overlay already covers the screen */
        }
        if (cancelled) return;
        timers.push(setTimeout(() => !cancelled && setPhase('out'), 450));
        timers.push(setTimeout(() => !cancelled && setPhase('done'), 950));
        return;
      }

      // First open: paint the overlay (still hidden behind the native splash)...
      setPhase('cover');

      // Pre-decode the logo so the 25 tiles paint instantly when the assembly
      // starts (decoding on the first animation frame is what made it janky).
      try {
        const img = new window.Image();
        img.src = '/docupro-icon.png';
        await img.decode();
      } catch {
        /* ignore — will decode on paint */
      }
      if (cancelled) return;

      // Hide the native splash, then start the assembly on the next frame.
      try {
        const { SplashScreen } = await import('@capacitor/splash-screen');
        await SplashScreen.hide({ fadeOutDuration: 200 });
      } catch {
        /* not available — overlay already covers the screen */
      }
      if (cancelled) return;
      // Remember we've shown it so later loads use the spinner instead.
      try {
        sessionStorage.setItem(SPLASH_SHOWN_KEY, '1');
      } catch {
        /* ignore */
      }
      requestAnimationFrame(() => {
        if (cancelled) return;
        setPhase('play');
        timers.push(setTimeout(() => !cancelled && setPhase('out'), 1900));
        timers.push(setTimeout(() => !cancelled && setPhase('done'), 2450));
      });
    })();

    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
    };
  }, []);

  if (phase === 'idle' || phase === 'done') return null;

  const className = [
    'dp-splash',
    isSpinner ? 'dp-splash--spinner' : '',
    !isSpinner && (phase === 'play' || phase === 'out') ? 'dp-splash--play' : '',
    phase === 'out' ? 'dp-splash--out' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={className} aria-hidden="true">
      {isSpinner ? (
        <div className="dp-splash-spinner" />
      ) : (
        <div className="dp-splash-logo" style={{ width: `${LOGO_PX}px`, height: `${LOGO_PX}px` }}>
          {TILES.map((t) => (
            <span key={t.key} className="dp-splash-tile" style={t.style} />
          ))}
        </div>
      )}
    </div>
  );
}
